import { cloneDeep, pick } from 'lodash';
import { useRef } from 'react';
import { getOperateDirection, getTrackFunc, IExportLayerOriginInfo, useFramework, useIdentity } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { translatePointAngle } from '@/utils/math-kit';
import { asyncTraverseTree } from '@/utils/tree-kit';
import { BaseLayerViewType, DirectionType, ScaleOperateType, type ILayerProps } from '@/version-types';
import { MouseOperate } from '../../mouse-operate';
import { useStickyInfo } from './use-sticky-info';

export const useScaleOperate = () => {
    const framework = useFramework();
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<IExportLayerOriginInfo>();
    const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');
    const sticky = useStickyInfo();
    return {
        beforeScale(layerId: string, _operateType: ScaleOperateType) {
            const layer = framework.service.flatLayer.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = framework.service.frame.exportLayerOriginInfo(layerId);
            framework.service.frame.__emitBeforeBaseOperate(layer, BaseLayerViewType.SCALE);
            sticky.before();
        },

        /**
         * 缩放
         *
         * @param id - ID
         * @param e - 可选；触摸事件对象
         * @param keepRatio - 可选；是否保持比例
         */
        scale(id: string, rotate: number, e: ITouchKitEvent, keepRatio: boolean) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            const scaleLayer = framework.service.flatLayer.getLayer(id);
            if (!scaleLayer) {
                mouseOperate.emitCancel();
                return;
            }

            const operateType = getOperateDirection(e.target) as ScaleOperateType;
            const directionX =
                operateType === ScaleOperateType.RB || operateType === ScaleOperateType.RT
                    ? DirectionType.POSITIVE
                    : DirectionType.OPPOSITE;
            const directionY =
                operateType === ScaleOperateType.RB || operateType === ScaleOperateType.LB
                    ? DirectionType.POSITIVE
                    : DirectionType.OPPOSITE;
            const reverse = operateType === ScaleOperateType.RT || operateType === ScaleOperateType.LB;

            if (e.isStart) {
                framework.service.editor.setIsBaseOperate(true);
                framework.service.frame.addOrUpdateLayer({
                    ...cloneDeep(scaleLayer),
                    __isBasicOperating: true,
                });
                mouseOperate.emitScaleStart({
                    layerId: id,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    operateType,
                    directionX,
                    directionY,
                    reverse,
                    keepRatio,
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
                return;
            }

            if (e.isEnd) {
                framework.service.editor.setIsBaseOperate(false);
                const latestLayer = framework.service.flatLayer.getLayer(id);
                if (latestLayer) {
                    latestLayer.__isBasicOperating = undefined;
                }
            }

            const scale = framework.service.edgelessCanvas.zoom;
            const startPoint = translatePointAngle(e.startPoint, -rotate);
            const currentPoint = translatePointAngle(e.currentPoint, -rotate);
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;

            const offsetX = ((currentPoint.x - startPoint.x) / scale) * directionX;
            const offsetY = ((currentPoint.y - startPoint.y) / scale) * directionY;
            mouseOperate.emitScaleBeforeUpdate({
                layerId: id,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                operateType,
                directionX,
                directionY,
                reverse,
                keepRatio,
                rawOffsetX: offsetX,
                rawOffsetY: offsetY,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });

            const { width, height, isMin } = framework.service.frame.updateLayerScale(
                id,
                offsetX,
                offsetY,
                pick(oldInfo.current!, ['width', 'height']),
                directionX,
                reverse,
                originInfo!,
                childOriginInfoMap,
                {
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                },
                keepRatio,
            );
            mouseOperate.emitScaleAfterUpdate({
                layerId: id,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                operateType,
                directionX,
                directionY,
                reverse,
                keepRatio,
                appliedOffsetX: offsetX,
                appliedOffsetY: offsetY,
                width,
                height,
                isMin,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });

            sticky.pending(id);
            if (e.isEnd) {
                sticky.clear();
                mouseOperate.emitScaleEnd({
                    layerId: id,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    operateType,
                    directionX,
                    directionY,
                    reverse,
                    keepRatio,
                    appliedOffsetX: offsetX,
                    appliedOffsetY: offsetY,
                    width,
                    height,
                    isMin,
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
                const oldLayer = oldInfo.current;
                // let hasEffect = false;
                asyncTraverseTree(oldLayer, async (layer) => {
                    await framework.service.behavior.get(layer)?.onBaseScaleEnd?.(layer.id);
                    // const result = await framework.service.behavior.get(layer)?.onBaseScaleEnd?.(layer.id);
                    // if (result && !hasEffect) {
                    //     hasEffect = true;
                    // }
                    // console.log('onBaseScaleEnd', { result, layer });
                }).then(() => {
                    // if (hasEffect) {
                    //     const layer = framework.service.flatLayer.getLayer(id, true);
                    //     if (layer) {
                    //         // 重新通过loadResource更新
                    //         framework.service.frame.addOrUpdateLayer(layer);
                    //     }
                    // }
                    // if (!stickyInfo.current) {
                    // const layer = framework.service.flatLayer.getLayer(id, true);
                    // framework.service.frame.addOrUpdateLayer(layer, layer.__originIndex, true);

                    if (sticky.hasSticky()) {
                        sticky.after(oldLayer);
                    } else {
                        // console.log('普通缩放结束');
                        framework.service.history.write(oldLayer, getTrackFunc(identity, oldLayer, 'scale'));
                    }
                    framework.service.agentLayout.arrangeLayerFrame(id);
                });
                // framework.service.frame.addOrUpdateLayer(framework.service.flatLayer.getLayer(id, true), {
                //     skipEdgelessUpdate: true,
                // });
                framework.service.frame.setActiveLayer(id);
            }
        },
    };
};
